import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiChevronDown, FiChevronRight, FiFilter, FiSearch, FiX
} from 'react-icons/fi';

import { Loader } from '@/components/loader/loader';
import type { TraceRequestFilters } from '@/hooks/queries/useTraces';
import {
  useTraceActionLogsQuery,
  useTraceModelLogsQuery,
  useTraceRequestsQuery,
  useTraceResponsesQuery
} from '@/hooks/queries/useTraces';
import type {
  ActionLog, LLMMessage, MessageChoice,ModelLog, RequestLog, ResponseLog
} from '@/types/logs';
import type { TimelineEntry, TraceEvent } from '@/types/trace';
import { formatISODateTime } from '@/utils/timeFormatter';

import styles from './traceViewer.module.scss';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Safely resolve a value that may be a JSON string or already an object */
function resolveJsonOrObject( value: unknown ): any {
  if ( typeof value === 'string' ) {
    try { return JSON.parse( value ); } catch { return value; }
  }
  return value;
}

function durationSeconds( start: string, end: string ): string {
  const s = DateTime.fromISO( start );
  const e = DateTime.fromISO( end );
  if ( !s.isValid || !e.isValid ) return '-';
  return e.diff( s, 'seconds' ).seconds.toFixed( 2 ) + 's';
}

function extractUserMessage( req: RequestLog ): string {
  const requestPayload = resolveJsonOrObject( req.meta_data?.request );
  const messages = requestPayload?.messages;
  if ( !Array.isArray( messages ) || messages.length === 0 ) return '-';
  const last = messages[ messages.length - 1 ];
  const text = last?.content?.text ?? last?.content;
  return typeof text === 'string' ? text : JSON.stringify( text );
}

function extractFinalResponse( responses: ResponseLog[] ): string {
  for ( let i = responses.length - 1; i >= 0; i-- ) {
    const responsePayload = resolveJsonOrObject( responses[ i ].meta_data?.response );
    const msgs = responsePayload?.messages;
    if ( !Array.isArray( msgs ) ) continue;
    for ( const m of msgs ) {
      if ( m?.content?.type === 'TextualContent' && m.content.text ) {
        return m.content.text;
      }
    }
  }
  return '-';
}

function truncate( s: string, max: number = 120 ): string {
  return s.length > max ? s.slice( 0, max ) + '...' : s;
}

// ── URL state type for shareable links ──────────────────────────────────────

export type TraceUrlState = {
  traceRequestId: string;
  traceCorrelationId: string;
  traceClientId: string;
  traceDateFrom: string;
  traceDateTo: string;
  traceKeyword: string;
  traceOffset: number;
  selectedRequest: string;
};

// ── Request List Panel ───────────────────────────────────────────────────────

interface RequestListProps {
  agentId: string;
  useLocalZone: boolean;
  initialUrlState?: TraceUrlState;
  onUrlStateChange?: ( state: TraceUrlState ) => void;
}

function RequestListPanel( { agentId, useLocalZone, initialUrlState, onUrlStateChange }: RequestListProps ) {
  const [ offset, setOffset ] = useState( initialUrlState?.traceOffset ?? 0 );
  const batchSize = 20;

  // Filter state — all server-side except keyword
  const [ filtersOpen, setFiltersOpen ] = useState( false );
  const [ filterRequestId, setFilterRequestId ] = useState( initialUrlState?.traceRequestId ?? '' );
  const [ filterCorrelationId, setFilterCorrelationId ] = useState( initialUrlState?.traceCorrelationId ?? '' );
  const [ filterClientId, setFilterClientId ] = useState( initialUrlState?.traceClientId ?? '' );
  const [ filterDateFrom, setFilterDateFrom ] = useState( initialUrlState?.traceDateFrom ?? '' );
  const [ filterDateTo, setFilterDateTo ] = useState( initialUrlState?.traceDateTo ?? '' );
  const [ keyword, setKeyword ] = useState( initialUrlState?.traceKeyword ?? '' );

  // Build server-side filters reactively — any change re-fetches
  const serverFilters = useMemo<TraceRequestFilters>( () => ( {
    requestId: filterRequestId || undefined,
    correlationId: filterCorrelationId || undefined,
    sessionId: filterClientId || undefined,
    startTime: filterDateFrom ? DateTime.fromISO( filterDateFrom ).startOf( 'day' ).toISO()! : undefined,
    endTime: filterDateTo ? DateTime.fromISO( filterDateTo ).endOf( 'day' ).toISO()! : undefined
  } ), [ filterRequestId, filterCorrelationId, filterClientId, filterDateFrom, filterDateTo ] );

  // Reset to page 1 when filters change
  const filterKey = JSON.stringify( serverFilters );
  const [ prevFilterKey, setPrevFilterKey ] = useState( filterKey );
  if ( filterKey !== prevFilterKey ) {
    setPrevFilterKey( filterKey );
    if ( offset !== 0 ) setOffset( 0 );
  }

  const clearFilters = useCallback( () => {
    setFilterRequestId( '' );
    setFilterCorrelationId( '' );
    setFilterClientId( '' );
    setFilterDateFrom( '' );
    setFilterDateTo( '' );
    setKeyword( '' );
    setOffset( 0 );
  }, [] );

  const hasServerFilter = filterRequestId || filterCorrelationId || filterClientId || filterDateFrom || filterDateTo;
  const hasAnyFilter = hasServerFilter || keyword;

  const { data, isLoading, isError } = useTraceRequestsQuery( agentId, batchSize, offset, serverFilters );
  const rawRequests = data?.completion_requests ?? [];

  // Client-side keyword filter only
  const requests = useMemo( () => {
    if ( !keyword.trim() ) return rawRequests;
    const kw = keyword.trim().toLowerCase();
    return rawRequests.filter( r => {
      const msg = extractUserMessage( r ).toLowerCase();
      const rid = ( r.request_id ?? r.id ?? '' ).toLowerCase();
      const cid = ( r.correlation_id ?? '' ).toLowerCase();
      const clientId = ( r.client_id ?? '' ).toLowerCase();
      return msg.includes( kw ) || rid.includes( kw ) || cid.includes( kw ) || clientId.includes( kw );
    } );
  }, [ rawRequests, keyword ] );

  const [ selectedRequestId, setSelectedRequestId ] = useState<string | null>( initialUrlState?.selectedRequest || null );
  const selectedRequest = requests.find( r => r.request_id === selectedRequestId || r.id === selectedRequestId );

  // Notify parent of state changes for URL sync
  const isInitialMount = useRef( true );
  useEffect( () => {
    if ( isInitialMount.current ) {
      isInitialMount.current = false;
      return;
    }
    onUrlStateChange?.( {
      traceRequestId: filterRequestId,
      traceCorrelationId: filterCorrelationId,
      traceClientId: filterClientId,
      traceDateFrom: filterDateFrom,
      traceDateTo: filterDateTo,
      traceKeyword: keyword,
      traceOffset: offset,
      selectedRequest: selectedRequestId ?? ''
    } );
  }, [ filterRequestId, filterCorrelationId, filterClientId, filterDateFrom, filterDateTo, keyword, offset, selectedRequestId, onUrlStateChange ] );

  return (
    <div className={ styles.traceViewer }>
      <div className={ styles.requestList }>
        <div className={ styles.requestListHeader }>
          <span>Requests</span>
          <button
            type="button"
            className={ `${ styles.filterToggle } ${ hasAnyFilter ? styles.filterToggleActive : '' }` }
            onClick={ () => setFiltersOpen( !filtersOpen ) }
            title="Toggle filters"
          >
            <FiFilter size={ 14 } />
          </button>
        </div>

        {filtersOpen && (
          <div className={ styles.filtersPanel }>
            <div className={ styles.filterRow }>
              <div className={ styles.searchBox }>
                <FiSearch size={ 13 } className={ styles.searchIcon } />
                <input
                  type="text"
                  placeholder="Keyword search..."
                  value={ keyword }
                  onChange={ e => setKeyword( e.target.value ) }
                  className={ styles.filterInput }
                />
              </div>
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>From</label>
              <input
                type="date"
                value={ filterDateFrom }
                onChange={ e => setFilterDateFrom( e.target.value ) }
                className={ styles.filterInput }
              />
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>To</label>
              <input
                type="date"
                value={ filterDateTo }
                onChange={ e => setFilterDateTo( e.target.value ) }
                className={ styles.filterInput }
              />
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>Request ID</label>
              <input
                type="text"
                placeholder="e.g. 458af480-..."
                value={ filterRequestId }
                onChange={ e => setFilterRequestId( e.target.value ) }
                className={ styles.filterInput }
              />
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>Correlation ID</label>
              <input
                type="text"
                placeholder="e.g. 817c36ab-..."
                value={ filterCorrelationId }
                onChange={ e => setFilterCorrelationId( e.target.value ) }
                className={ styles.filterInput }
              />
            </div>
            <div className={ styles.filterRow }>
              <label className={ styles.filterLabel }>Client ID</label>
              <input
                type="text"
                placeholder="e.g. aidp-studio"
                value={ filterClientId }
                onChange={ e => setFilterClientId( e.target.value ) }
                className={ styles.filterInput }
              />
            </div>
            {hasAnyFilter && (
              <div className={ styles.filterActions }>
                <button type="button" className={ styles.filterClear } onClick={ clearFilters }>
                  <FiX size={ 12 } /> Clear all
                </button>
              </div>
            )}
          </div>
        )}

        <div className={ styles.requestItems }>
          {isLoading ? (
            <div className={ styles.loading }>Loading...</div>
          ) : isError ? (
            <p className={ styles.error }>Error loading requests</p>
          ) : requests.length === 0 ? (
            <p className={ styles.empty }>
              {hasAnyFilter ? 'No requests match the current filters.' : 'No requests found. Traces will appear once this agent receives traffic.'}
            </p>
          ) : (
            requests.map( req => {
              const reqId = req.request_id ?? req.id;
              const isSelected = reqId === selectedRequestId;
              const userMsg = extractUserMessage( req );
              return (
                <button
                  key={ req.id }
                  type="button"
                  className={ `${ styles.requestItem } ${ isSelected ? styles.requestItemActive : '' }` }
                  onClick={ () => setSelectedRequestId( reqId ) }
                >
                  <div className={ styles.requestItemTime }>
                    {formatISODateTime( req.start_time, useLocalZone, 'M/d hh:mm:ss a' )}
                  </div>
                  <div className={ styles.requestItemMessage }>
                    {truncate( userMsg, 80 )}
                  </div>
                </button>
              );
            } )
          )}
        </div>
        <div className={ styles.pagination }>
          <button type="button" disabled={ offset === 0 } onClick={ () => setOffset( Math.max( 0, offset - batchSize ) ) }>
            Prev
          </button>
          <span>Page {Math.floor( offset / batchSize ) + 1}</span>
          <button type="button" disabled={ rawRequests.length < batchSize } onClick={ () => setOffset( offset + batchSize ) }>
            Next
          </button>
        </div>
      </div>

      <div className={ styles.traceDetail }>
        {selectedRequest ? (
          <TraceDetailPanel
            request={ selectedRequest }
            agentId={ agentId }
            useLocalZone={ useLocalZone }
          />
        ) : (
          <div className={ styles.selectPrompt }>Select a request to view its trace</div>
        )}
      </div>
    </div>
  );
}

// ── Trace Detail Panel (Gantt + sequential events) ───────────────────────────

interface TraceDetailPanelProps {
  request: RequestLog;
  agentId: string;
  useLocalZone: boolean;
}

function TraceDetailPanel( { request, agentId, useLocalZone }: TraceDetailPanelProps ) {
  const requestId = request.request_id ?? request.id;
  const callId = request.meta_data?.call_id as string | undefined;

  const { data: modelData, isLoading: modelsLoading } = useTraceModelLogsQuery( agentId, requestId );
  const { data: actionData, isLoading: actionsLoading } = useTraceActionLogsQuery( agentId, requestId );
  const { data: responseData, isLoading: responsesLoading } = useTraceResponsesQuery( callId ?? null );

  const modelLogs = modelData?.model_invocations ?? [];
  const actionLogs = actionData?.agent_actions ?? [];
  const responses = responseData?.completion_responses ?? [];

  const isLoading = modelsLoading || actionsLoading || responsesLoading;

  const userMessage = extractUserMessage( request );
  const finalResponse = extractFinalResponse( responses );

  // Compute the full request duration: from request start to the last response end
  const requestEndTime = useMemo( () => {
    const allEndTimes = [
      request.end_time || request.start_time,
      ...responses.map( r => r.end_time || r.start_time ).filter( Boolean ) as string[],
      ...modelLogs.map( m => m.end_time ).filter( Boolean )
    ].map( t => DateTime.fromISO( t ).toMillis() ).filter( t => !isNaN( t ) );
    if ( allEndTimes.length === 0 ) return request.end_time || request.start_time;
    return DateTime.fromMillis( Math.max( ...allEndTimes ) ).toISO()!;
  }, [ request, responses, modelLogs ] );

  // Build timeline entries for Gantt — sorted oldest to newest
  const timelineEntries = useMemo( (): TimelineEntry[] => {
    const entries: TimelineEntry[] = [];

    // User Request spans full duration (first request to last response)
    entries.push( {
      id: `req-${ request.id }`,
      kind: 'request',
      label: 'User Request',
      startTime: request.start_time,
      endTime: requestEndTime
    } );

    modelLogs.forEach( ( ml ) => {
      entries.push( {
        id: `model-${ ml.id }`,
        kind: 'model',
        label: `LLM: ${ ml.model }`,
        startTime: ml.start_time,
        endTime: ml.end_time
      } );
    } );

    actionLogs.forEach( ( al ) => {
      entries.push( {
        id: `action-${ al.id }`,
        kind: 'action',
        label: 'Action',
        startTime: al.start_time,
        endTime: al.end_time
      } );
    } );

    // Collapse all responses into a single row with individual segment bars
    const responsesWithTime = responses.filter( r => r.start_time );
    if ( responsesWithTime.length > 0 ) {
      const startTimes = responsesWithTime.map( r => DateTime.fromISO( r.start_time! ).toMillis() );
      const endTimes = responsesWithTime.map( r => DateTime.fromISO( r.end_time || r.start_time! ).toMillis() );
      entries.push( {
        id: 'resp-combined',
        kind: 'response',
        label: `Responses (${ responsesWithTime.length })`,
        startTime: DateTime.fromMillis( Math.min( ...startTimes ) ).toISO()!,
        endTime: DateTime.fromMillis( Math.max( ...endTimes ) ).toISO()!,
        segments: responsesWithTime.map( ( r, i ) => ( {
          id: `resp-seg-${ i }`,
          startTime: r.start_time!,
          endTime: r.end_time || r.start_time!
        } ) )
      } );
    }

    // Sort by start time (oldest first), keep request row at top
    entries.sort( ( a, b ) => {
      if ( a.kind === 'request' ) return -1;
      if ( b.kind === 'request' ) return 1;
      return DateTime.fromISO( a.startTime ).toMillis() - DateTime.fromISO( b.startTime ).toMillis();
    } );

    return entries;
  }, [ request, requestEndTime, modelLogs, actionLogs, responses ] );

  // Build sequential trace events sorted by time
  const traceEvents = useMemo( (): TraceEvent[] => {
    const events: TraceEvent[] = [];

    events.push( {
      id: `evt-req-${ request.id }`,
      kind: 'user_message',
      timestamp: request.start_time,
      title: 'User Message',
      detail: userMessage
    } );

    modelLogs.forEach( ( ml ) => {
      // Check if this LLM call includes tool calls
      const hasToolCalls = ml.choices?.some(
        ( c: MessageChoice ) => ( c.message as any )?.tool_calls?.length > 0
      );

      events.push( {
        id: `evt-model-${ ml.id }`,
        kind: 'llm_call',
        timestamp: ml.start_time,
        endTimestamp: ml.end_time,
        title: `LLM Call — ${ ml.model }`,
        detail: ml
      } );

      if ( hasToolCalls ) {
        const toolCalls = ml.choices?.flatMap(
          ( c: MessageChoice ) => ( c.message as any )?.tool_calls ?? []
        ) ?? [];
        toolCalls.forEach( ( tc: any, idx: number ) => {
          events.push( {
            id: `evt-tool-${ ml.id }-${ idx }`,
            kind: 'tool_call',
            timestamp: ml.end_time,
            title: `Tool Call — ${ tc.function?.name ?? 'unknown' }`,
            detail: tc
          } );
        } );
      }
    } );

    // Add action logs
    actionLogs.forEach( ( al ) => {
      const actionLabel = al.string_input?.[ 0 ]
        || ( al.meta_data?.user_message as string )
        || 'Agent Action';
      events.push( {
        id: `evt-action-${ al.id }`,
        kind: 'action',
        timestamp: al.start_time,
        endTimestamp: al.end_time,
        title: 'Action',
        detail: {
          label: actionLabel,
          agent: ( al as any ).agent ?? ( al as any ).agent_name,
          correlationId: al.correlation_id,
          meta: al.meta_data
        }
      } );
    } );

    // Add ALL responses in sequence
    responses.forEach( ( rl, idx ) => {
      const responsePayload = resolveJsonOrObject( rl.meta_data?.response );
      const msgs = responsePayload?.messages ?? [];
      const contentParts: string[] = [];
      let responseType = 'Response';

      for ( const m of msgs ) {
        const ct = m?.content?.type;
        if ( ct === 'TextualContent' && m.content.text ) {
          contentParts.push( m.content.text );
          responseType = 'Agent Response';
        } else if ( ct === 'TypingAction' ) {
          responseType = 'Typing Indicator';
        } else if ( m?.content ) {
          contentParts.push( typeof m.content === 'string' ? m.content : JSON.stringify( m.content ) );
        }
      }

      events.push( {
        id: `evt-resp-${ rl.id }`,
        kind: 'response',
        timestamp: rl.start_time || rl.end_time || request.start_time,
        title: `${ responseType } (${ idx + 1 }/${ responses.length })`,
        detail: contentParts.length > 0 ? contentParts.join( '\n' ) : `[${ responseType }]`
      } );
    } );

    events.sort( ( a, b ) => DateTime.fromISO( a.timestamp ).toMillis() - DateTime.fromISO( b.timestamp ).toMillis() );
    return events;
  }, [ request, modelLogs, actionLogs, responses, userMessage ] );

  // Compute topline metrics from model logs
  const totalPromptTokens = modelLogs.reduce( ( sum, ml ) => sum + ( ml.prompt_tokens || 0 ), 0 );
  const totalCompletionTokens = modelLogs.reduce( ( sum, ml ) => sum + ( ml.completion_tokens || 0 ), 0 );
  const totalCost = modelLogs.reduce( ( sum, ml ) => sum + ( ml.cost || 0 ), 0 );
  const totalDuration = durationSeconds( request.start_time, requestEndTime );

  if ( isLoading ) {
    return <div className={ styles.loading }>Loading trace...</div>;
  }

  return (
    <div className={ styles.detailContainer }>
      <div className={ styles.detailHeader }>
        <div className={ styles.detailMeta }>
          <span className={ styles.metaLabel }>Request ID:</span>
          <span className={ styles.metaValue }>{ requestId }</span>
        </div>
        <div className={ styles.detailMeta }>
          <span className={ styles.metaLabel }>Time:</span>
          <span className={ styles.metaValue }>
            {formatISODateTime( request.start_time, useLocalZone, 'M/d/yyyy hh:mm:ss a' )}
          </span>
        </div>
        <div className={ styles.detailSummary }>
          <div><strong>Input:</strong> {truncate( userMessage, 200 )}</div>
          <div><strong>Output:</strong> {truncate( finalResponse, 200 )}</div>
        </div>
        <div className={ styles.toplineMetrics }>
          <div className={ styles.metricCard }>
            <div className={ styles.metricValue }>{ totalPromptTokens.toLocaleString() }</div>
            <div className={ styles.metricLabel }>Prompt Tokens</div>
          </div>
          <div className={ styles.metricCard }>
            <div className={ styles.metricValue }>{ totalCompletionTokens.toLocaleString() }</div>
            <div className={ styles.metricLabel }>Completion Tokens</div>
          </div>
          <div className={ styles.metricCard }>
            <div className={ styles.metricValue }>${ totalCost.toFixed( 4 ) }</div>
            <div className={ styles.metricLabel }>Cost</div>
          </div>
          <div className={ styles.metricCard }>
            <div className={ styles.metricValue }>{ totalDuration }</div>
            <div className={ styles.metricLabel }>Total Time</div>
          </div>
          <div className={ styles.metricCard }>
            <div className={ styles.metricValue }>{ modelLogs.length }</div>
            <div className={ styles.metricLabel }>LLM Calls</div>
          </div>
        </div>
      </div>

      {/* Gantt Timeline */}
      <div className={ styles.ganttSection }>
        <div className={ styles.sectionTitle }>Timeline</div>
        <GanttChart entries={ timelineEntries } useLocalZone={ useLocalZone } />
      </div>

      {/* Sequential Events */}
      <div className={ styles.eventsSection }>
        <div className={ styles.sectionTitle }>Trace Events</div>
        <div className={ styles.eventsList }>
          {traceEvents.map( ( evt ) => (
            <TraceEventCard key={ evt.id } event={ evt } useLocalZone={ useLocalZone } />
          ) )}
        </div>
      </div>
    </div>
  );
}

// ── Gantt Chart ──────────────────────────────────────────────────────────────

interface GanttChartProps {
  entries: TimelineEntry[];
  useLocalZone: boolean;
}

function GanttChart( { entries, useLocalZone }: GanttChartProps ) {
  if ( entries.length === 0 ) return <p className={ styles.empty }>No timeline data</p>;

  const allTimes = entries.flatMap( e => [
    DateTime.fromISO( e.startTime ).toMillis(),
    DateTime.fromISO( e.endTime ).toMillis()
  ] ).filter( t => !isNaN( t ) );

  const minTime = Math.min( ...allTimes );
  const maxTime = Math.max( ...allTimes );
  const totalSpan = maxTime - minTime || 1;

  const kindColors: Record<string, string> = {
    request: '#2563eb',
    model: '#7c3aed',
    action: '#059669',
    response: '#d97706'
  };

  return (
    <div className={ styles.gantt }>
      {entries.map( entry => {
        const color = kindColors[ entry.kind ] || '#6b7280';
        const bars = entry.segments && entry.segments.length > 0
          ? entry.segments
          : [ { id: entry.id, startTime: entry.startTime, endTime: entry.endTime } ];

        return (
          <div key={ entry.id } className={ styles.ganttRow }>
            <div className={ styles.ganttLabel }>{entry.label}</div>
            <div className={ styles.ganttTrack }>
              {bars.map( seg => {
                const segStartMs = DateTime.fromISO( seg.startTime ).toMillis();
                const segEndMs = DateTime.fromISO( seg.endTime ).toMillis();
                const leftPct = Math.min( ( ( segStartMs - minTime ) / totalSpan ) * 100, 99 );
                const rawWidthPct = ( ( segEndMs - segStartMs ) / totalSpan ) * 100;
                // Point-in-time events (start===end) get a fixed pixel width so they're visible
                const isPointInTime = rawWidthPct < 1;

                return (
                  <div
                    key={ seg.id }
                    className={ styles.ganttBar }
                    style={ {
                      left: `${ leftPct }%`,
                      width: isPointInTime ? '6px' : `${ rawWidthPct }%`,
                      backgroundColor: color
                    } }
                    title={ `${ formatISODateTime( seg.startTime, useLocalZone, 'hh:mm:ss.SSS a' ) } — ${ durationSeconds( seg.startTime, seg.endTime ) }` }
                  />
                );
              } )}
            </div>
            <div className={ styles.ganttDuration }>
              {durationSeconds( entry.startTime, entry.endTime )}
            </div>
          </div>
        );
      } )}
      <div className={ styles.ganttLegend }>
        {Object.entries( kindColors ).map( ( [ kind, color ] ) => (
          <span key={ kind } className={ styles.legendItem }>
            <span className={ styles.legendDot } style={ { backgroundColor: color } } />
            {kind.charAt( 0 ).toUpperCase() + kind.slice( 1 )}
          </span>
        ) )}
      </div>
    </div>
  );
}

// ── Trace Event Card ─────────────────────────────────────────────────────────

interface TraceEventCardProps {
  event: TraceEvent;
  useLocalZone: boolean;
}

function TraceEventCard( { event, useLocalZone }: TraceEventCardProps ) {
  const [ expanded, setExpanded ] = useState( false );

  const kindIcons: Record<string, string> = {
    user_message: 'U',
    llm_call: 'L',
    tool_call: 'T',
    action: 'A',
    response: 'R'
  };

  const kindAccents: Record<string, string> = {
    user_message: '#2563eb',
    llm_call: '#7c3aed',
    tool_call: '#059669',
    action: '#059669',
    response: '#d97706'
  };

  const renderDetail = () => {
    if ( event.kind === 'user_message' || event.kind === 'response' ) {
      return <pre className={ styles.eventPre }>{ typeof event.detail === 'string' ? event.detail : JSON.stringify( event.detail, null, 2 ) }</pre>;
    }

    if ( event.kind === 'llm_call' ) {
      const ml = event.detail as ModelLog;
      return (
        <div className={ styles.llmDetail }>
          <div className={ styles.llmMeta }>
            <span>Model: {ml.model}</span>
            <span>Prompt tokens: {ml.prompt_tokens}</span>
            <span>Completion tokens: {ml.completion_tokens}</span>
            {ml.cost !== undefined && <span>Cost: ${ml.cost.toFixed( 6 )}</span>}
            <span>Duration: {durationSeconds( ml.start_time, ml.end_time )}</span>
          </div>
          <div className={ styles.llmMessages }>
            <div className={ styles.messagesTitle }>Messages</div>
            {renderLLMMessages( ml.messages )}
          </div>
          {ml.choices && ml.choices.length > 0 && (
            <div className={ styles.llmMessages }>
              <div className={ styles.messagesTitle }>Response</div>
              {ml.choices.map( ( choice: MessageChoice, i: number ) => (
                <div key={ i } className={ styles.messageItem }>
                  <span className={ styles.messageRole }>{choice.message.role}</span>
                  <pre className={ styles.messageContent }>
                    {choice.message.content || ( ( choice.message as any ).tool_calls ? JSON.stringify( ( choice.message as any ).tool_calls, null, 2 ) : '-' )}
                  </pre>
                </div>
              ) )}
            </div>
          )}
        </div>
      );
    }

    if ( event.kind === 'action' ) {
      const act = event.detail as any;
      return (
        <div className={ styles.toolDetail }>
          {act.label && <div><strong>Action:</strong> {act.label}</div>}
          {act.agent && <div><strong>Agent:</strong> {act.agent}</div>}
          {act.meta && <pre className={ styles.eventPre }>{JSON.stringify( act.meta, null, 2 )}</pre>}
        </div>
      );
    }

    if ( event.kind === 'tool_call' ) {
      const tc = event.detail as any;
      return (
        <div className={ styles.toolDetail }>
          <div><strong>Function:</strong> {tc.function?.name}</div>
          <pre className={ styles.eventPre }>{tc.function?.arguments || JSON.stringify( tc, null, 2 )}</pre>
        </div>
      );
    }

    return <pre className={ styles.eventPre }>{ JSON.stringify( event.detail, null, 2 ) }</pre>;
  };

  return (
    <div className={ styles.eventCard } style={ { borderLeftColor: kindAccents[ event.kind ] || '#6b7280' } }>
      <button type="button" className={ styles.eventHeader } onClick={ () => setExpanded( !expanded ) }>
        <span className={ styles.eventIcon } style={ { backgroundColor: kindAccents[ event.kind ] || '#6b7280' } }>
          {kindIcons[ event.kind ] || '?'}
        </span>
        <span className={ styles.eventTitle }>{event.title}</span>
        <span className={ styles.eventTime }>
          {formatISODateTime( event.timestamp, useLocalZone, 'hh:mm:ss.SSS a' )}
          {event.endTimestamp && ` (${ durationSeconds( event.timestamp, event.endTimestamp ) })`}
        </span>
        <span className={ styles.eventChevron }>
          {expanded ? <FiChevronDown /> : <FiChevronRight />}
        </span>
      </button>
      {expanded && (
        <div className={ styles.eventBody }>
          {renderDetail()}
        </div>
      )}
    </div>
  );
}

// ── LLM Message renderer ─────────────────────────────────────────────────────

function renderLLMMessages( messages: LLMMessage[] | string ) {
  if ( typeof messages === 'string' ) {
    try {
      const parsed = JSON.parse( messages );
      if ( Array.isArray( parsed ) ) return renderLLMMessages( parsed );
    } catch {
      return <pre className={ styles.eventPre }>{messages}</pre>;
    }
    return <pre className={ styles.eventPre }>{messages}</pre>;
  }

  if ( !Array.isArray( messages ) ) return <pre className={ styles.eventPre }>{JSON.stringify( messages, null, 2 )}</pre>;

  return (
    <div className={ styles.messagesContainer }>
      {messages.map( ( msg, i ) => (
        <div key={ i } className={ styles.messageItem }>
          <span className={ styles.messageRole }>{msg.role}</span>
          <pre className={ styles.messageContent }>
            {typeof msg.content === 'string'
              ? ( msg.content.length > 500 ? msg.content.slice( 0, 500 ) + '...' : msg.content )
              : JSON.stringify( msg.content, null, 2 )}
          </pre>
        </div>
      ) )}
    </div>
  );
}

// ── Export ────────────────────────────────────────────────────────────────────

interface AgentTraceViewerProps {
  agentId: string;
  useLocalZone: boolean;
  initialUrlState?: TraceUrlState;
  onUrlStateChange?: ( state: TraceUrlState ) => void;
}

export function AgentTraceViewer( { agentId, useLocalZone, initialUrlState, onUrlStateChange }: AgentTraceViewerProps ) {
  return (
    <RequestListPanel
      agentId={ agentId }
      useLocalZone={ useLocalZone }
      initialUrlState={ initialUrlState }
      onUrlStateChange={ onUrlStateChange }
    />
  );
}
